import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

const getSelectedOptionBackground = (primary) => {
  if (!primary) return "#0e0b1d";

  const normalized = primary.trim().toLowerCase();
  if (
    normalized === "#fff" ||
    normalized === "#ffffff" ||
    normalized === "white" ||
    normalized === "transparent"
  ) {
    return "#0e0b1d";
  }

  return primary;
};

const SelectFilter = ({
  label,
  name,
  options,
  value,
  hasIcon,
  className,
  controlMinHeight,
  dropdownIndicatorColor,
  onchange,
}) => {
  const currentOrganization = useSelector(
    (state) => state.auth.currentOrganization
  );
  const [internalValue, setInternalValue] = useState(null);
  const isControlled = value !== undefined;

  useEffect(() => {
    if (!isControlled && options.length) {
      setInternalValue((current) => {
        if (!current) return options[0];

        return (
          options.find((option) => option.value === current.value) ?? options[0]
        );
      });
    }
  }, [isControlled, options]);

  const selectedValue = isControlled ? value : internalValue;

  const styles = {
    option: (styles, { isDisabled, isFocused, isSelected, theme }) => {
      const selectedBackground = getSelectedOptionBackground(theme.colors.primary);

      return {
        ...styles,
        backgroundColor: isDisabled
          ? styles.backgroundColor
          : isSelected
            ? selectedBackground
            : isFocused
              ? theme.colors.primary25 || "rgba(14, 11, 29, 0.08)"
              : "white",
        color: isDisabled ? "#ccc" : isSelected ? "#ffffff" : "#0e0b1d",
        cursor: isDisabled ? "not-allowed" : "pointer",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        lineHeight: 1.4,
      };
    },

    control: (styles) => {
      return {
        ...styles,
        backgroundColor: "white",
        border: "none",
        boxShadow: "none",
        minHeight: controlMinHeight,

        ":hover": {
          ...styles[":hover"],
          border: "none",
        },
      };
    },
    input: (styles) => ({ ...styles, color: "#0e0b1d" }),
    singleValue: (styles) => ({
      ...styles,
      color: "#0e0b1d",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: "white",
      zIndex: 9999,
    }),
    menuPortal: (styles) => ({
      ...styles,
      zIndex: 9999,
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      color: dropdownIndicatorColor,
    }),
  };

  const { i18n } = useTranslation();
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const selectElement = (
    <Select
      className="basic-single border-0 select-control--menu-is-open"
      classNamePrefix="select"
      value={selectedValue}
      isDisabled={!options.length}
      isRtl={i18n.language === "ar" ? true : false}
      name={name}
      options={options}
      styles={styles}
      onChange={(option) => {
        if (!isControlled) {
          setInternalValue(option);
        }
        onchange?.(option);
      }}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
      theme={{
        colors: {
          primary: currentOrganization?.renderColor || "#0e0b1d",
        },
      }}
    />
  );

  return (
    <div className={`card-style select-filter ${className}`}>
      <p className="font_14 font-bold font-color-grey mb-0">{label}</p>
      {hasIcon ? (
        <div className="select-filter__control-row">
          <span className="icon" aria-hidden="true">
            <i className="icon__level icon__level--good"></i>
            <i className="icon__level icon__level--med"></i>
            <i className="icon__level icon__level--weak"></i>
          </span>
          <div className="select-filter__select">{selectElement}</div>
        </div>
      ) : (
        selectElement
      )}
    </div>
  );
};

export default SelectFilter;

SelectFilter.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      icon: PropTypes.string,
    })
  ),
  label: PropTypes.string,
  value: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.string,
  }),
  hasIcon: PropTypes.bool,
  className: PropTypes.string,
  name: PropTypes.string,
  onchange: PropTypes.func,
  dropdownIndicatorColor: PropTypes.string,
  controlMinHeight: PropTypes.number,
};

SelectFilter.defaultProps = {
  options: [],
  label: "",
  hasIcon: false,
  className: "",
  controlMinHeight: 36,
  dropdownIndicatorColor: "",
};
